'use client';

import { useRouter } from 'next/navigation';
import type { MouseEvent } from 'react';

import Link from '@eliadmin/core/src/components/link';
import { Icon } from '@eliadmin/core/src/components/nt/ui/icon';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { CartStep, useCartStepper } from '@eliadmin/core/src/hooks/cart/use-cart-stepper';
import { useLocalePathname } from '@eliadmin/core/src/hooks/use-locale-pathname';
import { arePathsEqual } from '@eliadmin/core/src/utils/url-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';

/**
 * Figma „Back“ of the checkout header (170:14477): caret 16 px #127A26 + UI/Button M #0E6A20, 6 px
 * gap. On the checkout steps „Späť do košíka“ (to the first step, through the stepper so the step
 * changes without a page load), on the confirmation „Späť do obchodu“ (174:15212) to the homepage.
 * Below 640 px the caret only, the label names the link.
 */
export function CheckoutBackLink({
  cartLabel,
  shopLabel,
  homeHref,
}: {
  cartLabel: string;
  shopLabel: string;
  homeHref: string;
}) {
  const pathname = useLocalePathname();
  const { setStep } = useCartStepper();
  const onCart = arePathsEqual(pathname, ROUTES.CART());
  const label = onCart ? cartLabel : shopLabel;

  const backToCart = (event: MouseEvent<HTMLAnchorElement>) => {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
    event.preventDefault();
    setStep(CartStep.DETAIL);
  };

  return (
    <Link
      href={onCart ? ROUTES.CART() : homeHref}
      prefetch={false}
      onClick={onCart ? backToCart : undefined}
      aria-label={label}
      className={cn(
        'nt-button-m flex items-center gap-1.5 self-center justify-self-start rounded-[8px] text-[#0E6A20] transition-colors hover:text-[#0B5719]',
        'focus-visible:shadow-focus focus-visible:outline-none',
        'max-sm:-m-2 max-sm:p-2',
      )}
    >
      <Icon name="caret-left" size={16} className="text-[#127A26] max-sm:size-5" />
      <span className="hidden whitespace-nowrap sm:inline">{label}</span>
    </Link>
  );
}

/**
 * „#cookies“ in the admin footer links opens the cookie settings – the event of the core cookie button
 * (`CookieConsentFab`), with its `?occ=true` fallback that keeps the other parameters (the step).
 */
export function CookieSettingsLink({ label, className }: { label: string; className?: string }) {
  const router = useRouter();
  const open = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    const detail = { handled: false };
    window.dispatchEvent(new CustomEvent('eliadmin:open-cookie-preferences', { detail }));
    if (detail.handled) return;
    const url = new URL(window.location.href);
    url.searchParams.set('occ', 'true');
    router.push(`${url.pathname}${url.search}${url.hash}`);
  };

  return (
    <a href="#cookies" role="button" onClick={open} className={className}>
      {label}
    </a>
  );
}
